import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const COLORS = [
  "#F4B744", // طلایی اکتیو لجند
  "#FBBF24", // طلایی ملایم
  "#D97706", // طلایی گرم
];

interface Shape {
  id: string;
  size: number;
  x: number;
  y: number;
  color: string;
  parallax: number;
  rotation: number;
  scale: number;
}

// Detect mobile device (simple width check)
function isMobile() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 640;
}

function getRandomShapes(count: number, mobile: boolean): Shape[] {
  return Array.from({ length: count }).map(() => ({
    id: crypto.randomUUID(),
    size: mobile ? 120 + Math.random() * 80 : 220 + Math.random() * 200,
    x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200),
    y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    parallax: mobile ? 1 : 20 + Math.random() * 20,
    rotation: Math.random() * 360,
    scale: 1 + Math.random() * 0.3,
  }));
}

export default function ParallaxShapes() {
  const mobile = isMobile();
  const shapeCount = mobile ? 2 : 4;
  const [shapes, setShapes] = useState<Shape[]>(() => getRandomShapes(shapeCount, mobile));
  const [offsets, setOffsets] = useState(shapes.map(() => ({ x: 0, y: 0 })));

  // Mouse parallax only on desktop
  useEffect(() => {
    if (mobile) return;
    const handleMouseMove = (e: MouseEvent) => {
      const mx = e.clientX;
      const my = e.clientY;
      const newOffsets = shapes.map((shape) => {
        const dx = (mx - window.innerWidth / 2) / shape.parallax;
        const dy = (my - window.innerHeight / 2) / shape.parallax;
        return {
          x: -dx,
          y: -dy,
        };
      });
      setOffsets(newOffsets);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [shapes, mobile]);

  // Subtle slow animation loop
  useEffect(() => {
    const interval = setInterval(() => {
      setShapes((prevShapes) =>
        prevShapes.map((shape) => ({
          ...shape,
          rotation: (shape.rotation + 0.5) % 360,
          scale: 1 + Math.sin(Date.now() / 2000 + shape.id.charCodeAt(0)) * 0.1,
        }))
      );
    }, 60);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-0 overflow-hidden bg-black pointer-events-none">
      {/* Subtle gold particles on pure black background */}
      {shapes.map((shape, i) => (
        <motion.div
          key={shape.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: shape.size,
            height: shape.size,
            backgroundColor: shape.color,
            top: 0,
            left: 0,
            opacity: mobile ? 0.04 : 0.08, // بسیار ملایم و لوکس بدون ایجاد کدری یا قهوه‌ای شدن
            filter: "blur(60px)",
            mixBlendMode: "screen",
          }}
          animate={{
            x: shape.x + (offsets[i]?.x || 0),
            y: shape.y + (offsets[i]?.y || 0),
            rotate: shape.rotation,
            scale: shape.scale,
          }}
          transition={{
            duration: 0.6,
            ease: "linear",
          }}
        />
      ))}
      {/* هاله ظریف و مدرن در بالای صفحه */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(244,183,68,0.06),transparent)] pointer-events-none" />
    </div>
  );
}
